import { z } from 'zod';
import { ObjectIdSchema } from '../../../../shared/object-id.schema';
/**
 * List Superadmin AI Usage Request Schema
 * GET /api/v2/superadmin-ai-usage
 *
 * Optional `tenantId` switches the response between two drill-down levels:
 * omitted → one rollup row per tenant; set → one row per source × model
 * inside that tenant. `dateFrom`/`dateTo` (ISO strings) bound the window on
 * `createdAt`; both absent means all time.
 */ export const ListSuperadminAiUsageRequestSchema = z.object({
    // ObjectId-validated: the raw value names a tenant DB connection, so a
    // crafted string must never reach ConnectionManager.
    tenantId: ObjectIdSchema.optional(),
    dateFrom: z.iso.datetime({
        offset: true
    }).optional(),
    dateTo: z.iso.datetime({
        offset: true
    }).optional()
});
/**
 * Top-level aggregates over the in-scope (optionally tenant-filtered) usage
 * events. All costs are integer micro-EUR (1 EUR = 1_000_000).
 * `priceMissingCount` counts events recorded without a price row — their cost
 * is 0, so the cost totals undercount by that many events.
 */ export const AiUsageSummarySchema = z.object({
    totalCostMicroEur: z.number(),
    generationCount: z.number(),
    embeddingCount: z.number(),
    transcriptionCount: z.number(),
    totalInputTokens: z.number(),
    totalOutputTokens: z.number(),
    totalTranscriptionSeconds: z.number(),
    priceMissingCount: z.number()
});
/**
 * Per-tenant rollup row for the top-level overview. `totalTokens` is
 * input + output; input already includes cached and cache-write tokens.
 */ export const AiUsageTenantRowSchema = z.object({
    tenantId: z.string(),
    tenantName: z.string(),
    eventCount: z.number(),
    totalCostMicroEur: z.number(),
    totalTokens: z.number(),
    transcriptionSeconds: z.number()
});
/**
 * Tenant drill-down row, one per source × model. `sourceLabel` collapses the
 * discriminated source split: the `featureKey` for features, `appId` (with
 * `/functionName` when present) for apps. `model` is the raw stored model
 * name — an AiModel key, embedding deployment, or transcription model.
 */ export const AiUsageSourceRowSchema = z.object({
    sourceLabel: z.string(),
    sourceKind: z.enum([
        'feature',
        'app'
    ]),
    model: z.string(),
    eventCount: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    totalCostMicroEur: z.number(),
    priceMissingCount: z.number()
});
/**
 * The response carries a `summary` scoped to the current selection plus
 * exactly one populated drill-down array: `tenants` when no `tenantId` was
 * given, `sources` when one was. The other array is empty.
 * `failedTenantCount` > 0 means that many tenant databases could not be read
 * during the overview fan-out — totals are incomplete, not wrong.
 */ export const ListSuperadminAiUsageResponseSchema = z.object({
    summary: AiUsageSummarySchema,
    failedTenantCount: z.number(),
    tenants: z.array(AiUsageTenantRowSchema),
    sources: z.array(AiUsageSourceRowSchema)
});

//# sourceMappingURL=list-superadmin-ai-usage.schema.js.map